<script lang="ts">
    import { OverlayScrollbars } from 'overlayscrollbars';
    import { goto } from '$app/navigation';
    import { Button, Skeleton } from '@allibee/components/basic';
    import { actionSignOut, getLauncher, makeWorkspaceName, signOut } from '$stores/auth';
    import { HeimdallService } from '$lib/api';
    import { onMount } from 'svelte';
    import LogoAllibee from '$lib/service/logo/LogoAllibee.svelte';
    import LauncherWorkspaceCard from './LauncherWorkspaceCard.svelte';
    import { _, locale } from 'svelte-i18n';
    import { page } from '$app/stores';
    import { base } from '$app/paths';
    import { dev } from '$app/environment';
    import { FooterHelpDesk } from '$lib/service';
    import LauncherCopyJa from '$lib/assets/launcher-copy-ja.svg?component';
    import LauncherCopy from '$lib/assets/launcher-copy-en.svg?component';

    const { transformUrl } = $page.data.master;

    let scrollAreaRef: HTMLDivElement;

    let launchers: MtmLauncherDto[] | null = null;
    let isLoading = true;
    let errorMessage = '';

    onMount(async () => {
        OverlayScrollbars(scrollAreaRef, {});

        try {
            const isSuperAdmin = await HeimdallService.listWorkspaceUserPermissionsV3().then(res => res.data.data.some(p => p.name === 'super_admin.workspace.read'));
            if (isSuperAdmin) {
                await signOut();
                goto(`${base}/super-admin`);
                return;
            }

            launchers = await getLauncher();

            // 워크스페이스가 없으면 워크스페이스 생성 페이지로 이동
            if (launchers.length === 0) {
                goto(transformUrl('/workspace/create'));
            }

            if (launchers.length === 1) {
                goto(transformUrl(`/${makeWorkspaceName(launchers[0].tenantId, launchers[0].workspaceId)}/`));
            }
        } catch (err) {
            console.error(err);
            errorMessage = err instanceof Error ? err.message : 'Unknown error';
            throw err;
        } finally {
            isLoading = false;
        }
    });

    const handleSignOut = async () => {
        await actionSignOut();
    };

    const handleCreateWorkspace = () => {
        goto(transformUrl('/workspace/create'));
    };
</script>

<div bind:this={scrollAreaRef} class="h-full w-full">
    <div class="relative flex h-full w-full flex-col items-center justify-center gap-2 bg-white px-5 py-20 md:justify-start">
        <div class="flex w-full flex-col items-center md:w-[27.5rem]">
            <LogoAllibee class="h-6" />

            {#if isLoading}
                <p class="mt-7 whitespace-pre-wrap text-base text-gray-500" aria-label={$_('routes.launcher.loadingWorkspaceAndProducts')}>
                    {$_('routes.launcher.loadingWorkspaceAndProducts')}
                </p>
                <Skeleton class="mt-[2.75rem] h-32 w-[25rem]" />
            {:else}
                <div class="mt-7 flex flex-col items-center gap-1">
                    <p class="py-[0.3125rem]">
                        {#if $locale === 'ja'}
                            <LauncherCopyJa />
                        {:else}
                            <LauncherCopy />
                        {/if}
                    </p>
                    <p class="text-secondary-900 whitespace-pre-wrap text-center">{$_('apps.contract.routes.launcher.subtitle')}</p>
                </div>
                <div class="mt-[2.75rem] w-full">
                    <div class="flex w-full flex-col gap-3 px-5 md:w-[27.5rem]">
                        {#if launchers}
                            {#each launchers as launcher, index}
                                <a href={transformUrl(`/${makeWorkspaceName(launcher.tenantId, launcher.workspaceId)}/`)}>
                                    <!-- plugin 에 대해서 어떻게 할지가 정해지지 않음 -->
                                    <LauncherWorkspaceCard
                                        username={launcher.displayName}
                                        workspaceName={launcher.workspaceName}
                                        hasAiPlugin={false}
                                        subscriptionStatus={launcher.subscriptionStatus}
                                    />
                                </a>
                                {#if dev && launchers.length - 1 === index}
                                    <div
                                        class="inline-flex w-full items-center justify-start gap-4 rounded-lg border border-gray-100 bg-white p-4 text-left shadow-lg hover:bg-gray-100"
                                        on:click={handleCreateWorkspace}
                                        on:keydown={e => e.key === 'Enter' && handleCreateWorkspace()}
                                        role="button"
                                        tabindex="0"
                                    >
                                        <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
                                            <svg class="h-6 w-6" viewBox="0 0 36 37" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path
                                                    d="M17.9945 9.5957V18.4926M17.9945 18.4926V27.3895M17.9945 18.4926H26.8914M17.9945 18.4926H9.09766"
                                                    stroke="#0C0C0F"
                                                    stroke-width="2.07594"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                />
                                            </svg>
                                        </div>
                                        <div class="shrink grow basis-0 text-sm font-medium text-gray-900">only dev 테스트 워크스페이스 생성</div>
                                    </div>
                                {/if}
                            {:else}
                                <div
                                    class="inline-flex w-full items-center justify-start gap-4 rounded-lg border border-gray-100 bg-white p-4 text-left shadow-lg hover:bg-gray-100"
                                    on:click={handleCreateWorkspace}
                                    on:keydown={e => e.key === 'Enter' && handleCreateWorkspace()}
                                    role="button"
                                    tabindex="0"
                                >
                                    <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
                                        <svg class="h-6 w-6" viewBox="0 0 36 37" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M17.9945 9.5957V18.4926M17.9945 18.4926V27.3895M17.9945 18.4926H26.8914M17.9945 18.4926H9.09766"
                                                stroke="#0C0C0F"
                                                stroke-width="2.07594"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            />
                                        </svg>
                                    </div>
                                    <div class="shrink grow basis-0 text-sm font-medium text-gray-900">
                                        {$_('routes.launcher.actionCreateWorkspace')}
                                    </div>
                                </div>
                            {/each}
                        {:else if errorMessage}
                            <p class="mt-3 whitespace-pre-wrap text-center text-base leading-6 text-neutral-900">
                                {errorMessage}
                            </p>
                        {/if}
                    </div>
                </div>
            {/if}
            <div class="mt-10 flex flex-col gap-6">
                <button class="text-sm font-medium text-gray-900" on:click={handleSignOut}>
                    {$_('routes.launcher.actionLogout')}
                </button>
                <FooterHelpDesk />
            </div>
        </div>
    </div>
</div>
